/**
 * This slice is dedicated to Authentication API data
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import LibraryConfig from '../../../configLib';
import { CallStatus } from '../../enums';
import { IGNORE_GATEWAY_CONNECTION } from '../../libSettings';
import {
  AuthLoginData,
  ExtraUserDetails,
} from '../../models/auth/types';
import { UserTypeRelation } from '../../models/crm/types';
import { ENROLL_REQUEST_STATE, EnrollAuthDataState, IndividualExtendedInfoData } from '../../models/enroll';
import {
  ENROLL_AUTH_REQUEST_TYPE,
  EnrollAuthRequestBody,
  REQUEST_CALL_NAME_CONFIRM_EMAIL,
  REQUEST_CALL_NAME_REDEEM_SIGNUP_TOKEN,
} from '../../models/enroll-requests';
import CustomerState from '../../store-util/CustomerState';
import { appendToErrorsLog, getServerError } from '../../util/error-handling/ErrorHandlingHelpers';
import { NullableNumber, NullableString, WebAuthUserData } from '../../util/types';
import {
  applicationStartup,
  autoLoginFailed,
  autoLoginSuccess,
  enrollAuthCompleted,
  ErrorPayloadFull,
  gatewayConnected,
  gatewayDisconnected,
  individualExtendedInfoFailed,
  individualExtendedInfoSuccess,
  login,
  loginFailed,
  logout,
} from '../common-actions';
import { parseOnboardingErrorCode } from '../crm/helpers';
import { processErrorInReducer } from '../helpers';
import {
  isUserVerifiedStatus,
} from '../selectors';

export interface AuthState {
  status: 'idle' | 'refreshing' | 'connecting' | 'ready' | 'cancelled' | 'redirectsignup';
  skipRefresh: boolean;
  enroll: EnrollAuthDataState; // TODO: Sensitive data (ART-186)
  enrollUserId: NullableNumber;
  enrollPending: boolean;
  error: string | null;
  errorData: any;
  errorsLog: string[];
  userData?: WebAuthUserData | null;
  createIndividualAttempts: number;
  isForceRedirectToEnroll: boolean;
  enrollPortalStatus: Record<string, number>;
  autoLoginSuccessURL: NullableString;
  cookieSessionValid: boolean | null;
  bffClaims: Array<{ type: string; value: any }> | null;
}

const getNumberValue = (value: unknown): NullableNumber => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() && !Number.isNaN(Number(value))) return Number(value);

  return null;
};

const getEnrollUserIdFromRegisterPayload = (payload: any): NullableNumber => {
  const payloadItems = [
    payload,
    payload?.data,
    payload?.result,
    ...(Array.isArray(payload) ? payload : []),
    ...(Array.isArray(payload?.data) ? payload.data : []),
    ...(Array.isArray(payload?.result) ? payload.result : []),
  ];

  for (const item of payloadItems) {
    const userId = getNumberValue(item?.user_id)
      ?? getNumberValue(item?.userId)
      ?? getNumberValue(item?.user_correlation_id)
      ?? getNumberValue(item?.id);

    if (userId != null) return userId;
  }

  return null;
};

export const INITIAL_STATE: AuthState = {
  status: 'idle',
  skipRefresh: false,
  enroll: {
    status: ENROLL_REQUEST_STATE.INITIAL,
    confirmedSteps: {},
  } as EnrollAuthDataState,
  enrollUserId: null,
  enrollPending: false,
  error: null,
  errorData: null,
  errorsLog: [],
  userData: null,
  createIndividualAttempts: 0,
  isForceRedirectToEnroll: false,
  autoLoginSuccessURL: undefined,
  cookieSessionValid: null,
  bffClaims: null,
  enrollPortalStatus: {
    status: CallStatus.INITIAL,
  },
};

// TODO: The web part needs to call the same method for expired link check as well.
const isLinkExpired = (state: EnrollAuthDataState): boolean => {
  const { requestCallName } = state;

  return (
    state.status === ENROLL_REQUEST_STATE.ERROR
    && parseOnboardingErrorCode(state.statusCode!) === ENROLL_REQUEST_STATE.REQUEST_ERROR
    && [ REQUEST_CALL_NAME_CONFIRM_EMAIL, REQUEST_CALL_NAME_REDEEM_SIGNUP_TOKEN ].includes(requestCallName!));
};

const authSlice = createSlice({
  name: 'auth',
  initialState: INITIAL_STATE,
  reducers: {
    setCookieSessionValid(state, action: PayloadAction<boolean>) {
      state.cookieSessionValid = action.payload;
      state.status = action.payload ? 'ready' : 'idle';
    },
    setBffClaims(state, action: PayloadAction<Array<{ type: string; value: any }>>) {
      state.bffClaims = action.payload;

      const userRelationValue = Number(action.payload.find(claim => claim.type === 'user_relation')?.value);
      const userTypeRelation = Object.values(UserTypeRelation).includes(userRelationValue)
        ? (userRelationValue as UserTypeRelation)
        : null;

      CustomerState.setCustomerData({ userTypeRelation });
    },
    loginSucceeded(state, action) {
      const {
        userData,
      } = action.payload;

      state.userData = userData;
      state.status = 'ready';
      if (state.enrollPending) {
        state.enroll.status = ENROLL_REQUEST_STATE.READY;
      }
    },
    enrollAuth(state, action) {
      const { requestType } = action.payload as EnrollAuthRequestBody;
      state.enroll.lastType = requestType;

      switch (requestType) {
        // TODO: Remove this enum value and related code with ART-1023
        case ENROLL_AUTH_REQUEST_TYPE.RECOVER: {
          state.enrollPending = true;
          state.enroll.status = ENROLL_REQUEST_STATE.READY;
          return;
        }

        case ENROLL_AUTH_REQUEST_TYPE.REGISTER:
        case ENROLL_AUTH_REQUEST_TYPE.RESEND_EMAIL: {
          const {
            first_name,
            last_name,
            country_of_residence,
          } = (action.payload?.extraDetails || {}) as ExtraUserDetails;
          if (requestType === ENROLL_AUTH_REQUEST_TYPE.REGISTER && first_name && last_name && country_of_residence) {
            state.enroll.firstName = first_name;
            state.enroll.lastName = last_name;
            state.enroll.country = country_of_residence;
          }
          // The plan, the auto pass flag and the referral code the user actually arrived with are only
          // known at registration time, so keep them for the resend, which must repeat the same values
          // - or none at all, if nothing was chosen.
          if (requestType === ENROLL_AUTH_REQUEST_TYPE.REGISTER) {
            state.enroll.planId = action.payload.planId ?? null;
            state.enroll.autoPass = action.payload.autoPass ?? false;
            state.enroll.referralCode = action.payload.referralCode ?? null;
          }
          state.enroll.email = action.payload.email;
          if (country_of_residence) {
            state.enroll.emailToConfirm = undefined;
            state.enroll.confirmedSteps = {};
          }

          if (action.payload.referrerClientId) {
            state.enroll.referrerClientId = action.payload.referrerClientId;
          }
          break;
        }

        case ENROLL_AUTH_REQUEST_TYPE.CONFIRM_EMAIL: {
          const { firstName, lastName, country } = action.payload;
          state.enrollPending = true;
          state.enroll.confirmedSteps.email = false;
          if (firstName) { // fixes ART-1314 - the data is deleted if not present in payload
            state.enroll.firstName = firstName;
            state.enroll.lastName = lastName;
            state.enroll.country = country;
          }
          if (action.payload.referrerClientId) {
            state.enroll.referrerClientId = action.payload.referrerClientId;
          }
          break;
        }
        case ENROLL_AUTH_REQUEST_TYPE.REDEEM_SIGNUP_TOKEN: {
          const {
            firstName,
            lastName,
            country,
            username,
            referrerClientId,
          } = action.payload;
          state.enrollPending = true;
          state.enroll.confirmedSteps.email = false;
          state.enroll.email = username;
          if (firstName) {
            state.enroll.firstName = firstName;
            state.enroll.lastName = lastName;
            state.enroll.country = country;
          }
          if (referrerClientId) {
            state.enroll.referrerClientId = referrerClientId;
          }
          break;
        }
        case ENROLL_AUTH_REQUEST_TYPE.SEND_PHONE_CODE: {
          const { phoneCode, phoneNumber, userId } = action.payload;
          if (userId) {
            state.enroll.userId = userId;
            state.enrollUserId = userId;
          }
          state.enroll.phoneCode = phoneCode;
          state.enroll.phoneNumber = phoneNumber;
          break;
        }

        default:
          break;
      }
      state.enroll.errorCode = null;
      state.enroll.errorDescription = null;
      state.enroll.status = ENROLL_REQUEST_STATE.PENDING;
    },
    enrollAuthFailed(state, action) {
      const serverError = getServerError(action.payload.errorData);
      state.enroll.statusCode = action.payload.status;
      state.enroll.errorCode = serverError?.code ?? null;
      state.enroll.errorDescription = serverError?.description ?? null;
      state.enroll.status = ENROLL_REQUEST_STATE.ERROR;
      state.enroll.requestCallName = action.payload.callName;
      state.enroll.isAuthLinkExpired = isLinkExpired(state.enroll);
      appendToErrorsLog(action.payload.errorStr, state);
    },
    sendResetPassword(state, action) {},
    sendNonAppropriatenessEmail(state: AuthState) {
      state.enroll.status = ENROLL_REQUEST_STATE.INITIAL;
    },
    sendNonAppropriatenessEmailSuccess(state: AuthState) {
      state.enroll.status = ENROLL_REQUEST_STATE.READY;
    },
    sendNonAppropriatenessEmailFail(state: AuthState, action: PayloadAction<ErrorPayloadFull>) {
      state.enroll.status = ENROLL_REQUEST_STATE.ERROR;
      state.enroll.statusCode = action.payload.status;
    },
  },
  extraReducers: {
    [applicationStartup.type]: (state, action) => {
      state.status = 'idle';
      state.enroll.status = ENROLL_REQUEST_STATE.INITIAL; // needed to reset enroll status and use persisted enroll data
      state.enroll.emailToConfirm = undefined;
      if (action.payload) state.enrollPending = false;
    },
    [login.type]: (state, action) => {
      if (LibraryConfig.isWeb) {
        switch (action.payload) {
          case 'login':
            break;

          case 'callback':
            break;

          default:
            console.warn(`[auth/index] login - unknown case '${action.payload}'`);
            break;
        }
      }

      if (action.payload?.isEnroll) {
        state.enrollPending = true;
        state.enroll.status = ENROLL_REQUEST_STATE.PENDING;
      } else {
        state.enrollPending = false;
      }
      state.status = 'connecting';
    },
    [loginFailed.type]: (state, action) => {
      processErrorInReducer(state, action);
      state.cookieSessionValid = false;
      state.status = 'idle';
      if (state.enrollPending) {
        state.enroll.status = ENROLL_REQUEST_STATE.ERROR;
      }
      const errorMessage = action.payload?.errorStr || action.payload?.error || 'n/a';
      const errorData = action.payload?.errorData;
      const CANCELLED_ERROR_MSG_ANDROID = 'error: user cancelled flow';
      const CANCELLED_ERROR_MSG_IOS = 'org.openid.appauth.general error -3';
      const SIGNUP_ERROR_MSG_IOS = 'error: state mismatch, expecting';
      const SIGNUP_ERROR_MSG_ANDROID = 'error: data intent is null';

      if (errorData) {
        const errorStr = errorData.toString().toLowerCase();
        if (errorStr.indexOf(CANCELLED_ERROR_MSG_IOS) >= 0 || errorStr.indexOf(CANCELLED_ERROR_MSG_ANDROID) >= 0) {
          state.status = 'cancelled';
        } else if (errorStr.indexOf(SIGNUP_ERROR_MSG_IOS) >= 0 || errorStr.indexOf(SIGNUP_ERROR_MSG_ANDROID) >= 0) {
          state.status = 'redirectsignup';
        }
      }

      appendToErrorsLog(errorMessage, state);
    },
    [individualExtendedInfoSuccess.type]: (state, action) => {
      state.enroll.userId = (action.payload as IndividualExtendedInfoData).user_correlation_id;
      state.enrollUserId = (action.payload as IndividualExtendedInfoData).user_correlation_id;
      if (isUserVerifiedStatus(action.payload)) {
        state.enrollPending = false;
      }
    },
    [gatewayConnected.type]: state => {
      state.status = 'ready';
    },
    [gatewayDisconnected.type]: state => {
      if (!IGNORE_GATEWAY_CONNECTION) state.status = 'idle';
    },
    [logout.type]: (state, action) => ({
      ...INITIAL_STATE,
      errorsLog: state.errorsLog,
      enrollUserId: action.payload?.preserveEnrollUserId
        ? (state.enrollUserId ?? state.enroll.userId ?? null)
        : null,
    }),
    [autoLoginSuccess.type]: (state, action: PayloadAction<AuthLoginData|undefined>) => {
      state.autoLoginSuccessURL = action.payload?.successURL;
    },
    [autoLoginFailed.type]: state => {
      state.autoLoginSuccessURL = undefined;
      state.status = 'idle';
    },
    [enrollAuthCompleted.type]: (state, action) => {
      const { callName } = action.payload;
      const requestType = ENROLL_AUTH_REQUEST_TYPE[callName.split('/')[1]] as any;

      switch (requestType) {
        case ENROLL_AUTH_REQUEST_TYPE.REGISTER: {
          const userId = getEnrollUserIdFromRegisterPayload(action.payload);
          if (userId != null) {
            state.enroll.userId = userId;
            state.enrollUserId = userId;
          }
          break;
        }

        case ENROLL_AUTH_REQUEST_TYPE.CONFIRM_EMAIL:
        case ENROLL_AUTH_REQUEST_TYPE.REDEEM_SIGNUP_TOKEN:
          state.enroll.confirmedSteps.email = true;
          break;

        case ENROLL_AUTH_REQUEST_TYPE.VERIFY_PHONE_CODE:
          state.enroll.confirmedSteps.phone = true;
          break;

        case ENROLL_AUTH_REQUEST_TYPE.SEND_PHONE_CODE:
          state.enroll.confirmedSteps.didSendPhone = true;
          break;

        case ENROLL_AUTH_REQUEST_TYPE.SET_CLIENTS:
          state.enroll.clients = action.payload;
          break;

        default:
          break;
      }

      state.enroll.status = ENROLL_REQUEST_STATE.READY;
    },
    [individualExtendedInfoFailed.type]: (state, action) => {
      state.createIndividualAttempts++;
    },
  },
});

export const {
  loginSucceeded,
  setCookieSessionValid,
  setBffClaims,
  // enroll
  enrollAuth,
  enrollAuthFailed,
  sendResetPassword,
  sendNonAppropriatenessEmail,
  sendNonAppropriatenessEmailSuccess,
  sendNonAppropriatenessEmailFail,
} = authSlice.actions;

export default authSlice.reducer;
